/**
 * Bejövő számlák — kártya nézet, reszponzív, NAV szinkronizáció
 */
import { useState, useEffect } from 'react';
import { get, post, patch, downloadFile } from '../../lib/api';
import { PageHeader, Button, Badge, Card, Input, Modal, EmptyState, LoadingOverlay } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import ActionsDropdown from '../../components/ActionsDropdown';
import PartnerAutocomplete from '../../components/PartnerAutocomplete';
import MoneyInput from '../../components/ui/MoneyInput';

interface IncomingInvoice {
  id: string; supplier_invoice_number: string; source: string; status: string;
  supplier_partner_id?: string; supplier_name?: string; partner_name?: string;
  issue_date?: string; due_date?: string; performance_date?: string;
  subtotal_net?: number; subtotal_vat?: number; subtotal_gross?: number;
  payment_method?: string; currency?: string;
  attachment_id?: string;
}

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'info' | 'success' | 'warning' | 'error' }> = {
  received: { label: 'Beérkezett', variant: 'default' },
  verified: { label: 'Igazolva', variant: 'info' },
  paid: { label: 'Fizetve', variant: 'success' },
  partially_paid: { label: 'Részben fizetve', variant: 'warning' },
  disputed: { label: 'Vitatott', variant: 'error' },
};

function fmtPrice(f: number | string | undefined) { return f ? Math.round(Number(f) / 100).toLocaleString('hu-HU') + ' Ft' : '—'; }
function fmtDate(iso: string | undefined) { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return '—'; } }

export default function IncomingInvoicesPage() {
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<IncomingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showNavQuery, setShowNavQuery] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [page, setPage] = useState(1);
  const pageSize = 25;

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const d = await get<any>(`/incoming-invoices?${params}`);
      setInvoices(Array.isArray(d) ? d : d?.data || []);
    } catch { /* */ }
    setLoading(false);
  }

  useEffect(() => { load(); }, [statusFilter]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]);

  // Dátumszűrés kliens-oldalon
  const filtered = invoices.filter(inv => {
    if (dateFrom && (inv.issue_date || '').slice(0, 10) < dateFrom) return false;
    if (dateTo && (inv.issue_date || '').slice(0, 10) > dateTo) return false;
    return true;
  });

  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize);

  const counts = {
    all: invoices.length,
    received: invoices.filter(i => i.status === 'received').length,
    verified: invoices.filter(i => i.status === 'verified').length,
    paid: invoices.filter(i => i.status === 'paid').length,
  };

  return (
    <div>
      <PageHeader title="Bejövő számlák" description={`${filtered.length} / ${invoices.length} számla`}
        actions={
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button variant="ghost" onClick={() => setShowNavQuery(true)}>NAV szinkronizáció</Button>
            <Button onClick={() => setShowManual(true)}>+ Manuális rögzítés</Button>
          </div>
        }
      />

      {/* Szűrők */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Input placeholder="Keresés sorszám, beszállító..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ flex: 1, minWidth: '180px' }} />
        <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} style={{ width: '130px', fontSize: '12px' }} />
        <span style={{ color: 'var(--slate)', fontSize: '12px' }}>—</span>
        <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} style={{ width: '130px', fontSize: '12px' }} />
        <button onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
          style={{ fontSize: '11px', color: 'var(--orange)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>Mind</button>
      </div>

      {/* Státusz szűrők */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {[
          { key: '', label: `Mind (${counts.all})` },
          { key: 'received', label: `Beérkezett (${counts.received})` },
          { key: 'verified', label: `Igazolva (${counts.verified})` },
          { key: 'paid', label: `Fizetve (${counts.paid})` },
        ].map(f => (
          <button key={f.key} onClick={() => { setStatusFilter(f.key); setPage(1); }}
            style={{
              padding: '4px 10px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)',
              background: statusFilter === f.key ? 'var(--navy)' : 'var(--white)',
              color: statusFilter === f.key ? '#fff' : 'var(--ink)',
              cursor: 'pointer', fontWeight: statusFilter === f.key ? 600 : 400,
            }}>{f.label}</button>
        ))}
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon="📥" title="Nincs bejövő számla" description="Szinkronizáld a NAV-ról vagy rögzíts egyet manuálisan."
          action={<Button size="sm" onClick={() => setShowNavQuery(true)}>NAV szinkronizáció</Button>} />
      ) : (
        <>
          <div style={{ display: 'grid', gap: '8px' }}>
            {paginated.map(inv => {
              const st = STATUS_MAP[inv.status] || STATUS_MAP.received;
              const supplierName = inv.partner_name || inv.supplier_name || '—';
              const isOverdue = inv.due_date && new Date(inv.due_date) < new Date() && inv.status !== 'paid';
              const isNav = inv.source === 'nav_sync' || inv.source === 'nav_query';

              return (
                <Card key={inv.id} style={{
                  borderLeft: isOverdue ? '3px solid var(--red)' : undefined,
                  padding: '12px 16px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                        <span style={{ fontSize: '14px' }}>{isNav ? '🏛️' : '📥'}</span>
                        <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                          {inv.supplier_invoice_number}
                        </span>
                        <Badge variant={st.variant}>{st.label}</Badge>
                        {isNav && <Badge variant="info" style={{ fontSize: '9px' }}>NAV</Badge>}
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink)' }}>{supplierName}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '12px', color: 'var(--slate)', marginTop: '4px' }}>
                        <span>Kelt: {fmtDate(inv.issue_date)}</span>
                        {inv.due_date && <span style={{ color: isOverdue ? 'var(--red)' : undefined, fontWeight: isOverdue ? 600 : undefined }}>
                          Esedékes: {fmtDate(inv.due_date)}{isOverdue ? ' (lejárt!)' : ''}
                        </span>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: '16px', color: 'var(--navy)' }}>{fmtPrice(inv.subtotal_gross)}</div>
                      {inv.subtotal_net && (
                        <div style={{ fontSize: '11px', color: 'var(--slate)' }}>
                          nettó: {fmtPrice(inv.subtotal_net)} + ÁFA: {fmtPrice(inv.subtotal_vat)}
                        </div>
                      )}
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                      <ActionsDropdown actions={[
                        ...(inv.attachment_id ? [
                          { label: 'PDF letöltés', icon: '📥', onClick: async () => {
                            try { await downloadFile(`/incoming-invoices/${inv.id}/pdf`, `${inv.supplier_invoice_number || inv.id}.pdf`); } catch (e: any) { toast(e.message || 'PDF hiba.', 'error'); }
                          }},
                        ] : []),
                        ...(inv.status === 'received' ? [
                          { label: 'Igazolás', icon: '✅', onClick: async () => {
                            try { await patch(`/incoming-invoices/${inv.id}`, { status: 'verified' }); toast('Igazolva.', 'success'); load(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                          }},
                        ] : []),
                        ...(inv.status !== 'paid' ? [
                          { label: 'Fizetve', icon: '💰', onClick: async () => {
                            try { await patch(`/incoming-invoices/${inv.id}`, { status: 'paid' }); toast('Fizetve.', 'success'); load(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                          }},
                        ] : []),
                      ]} />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Lapozás */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: '12px' }}>← Előző</button>
              <span style={{ fontSize: '13px', color: 'var(--slate)' }}>{page} / {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: '12px' }}>Következő →</button>
            </div>
          )}
        </>
      )}

      {/* NAV szinkronizáció modal */}
      {showNavQuery && <NavSyncModal onClose={() => setShowNavQuery(false)} onComplete={() => { setShowNavQuery(false); load(); }} />}

      {/* Manuális rögzítés modal */}
      {showManual && <ManualEntryModal onClose={() => setShowManual(false)} onCreated={() => { setShowManual(false); load(); }} />}
    </div>
  );
}

// ── NAV szinkronizáció modal ──

function NavSyncModal({ onClose, onComplete }: { onClose: () => void; onComplete: () => void }) {
  const { toast } = useToast();
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState<{ processed: number; skipped: number; errors: string[]; total: number } | null>(null);

  async function handleSync() {
    setSyncing(true);
    try {
      const r = await post<any>('/incoming-invoices/nav-query', { fromDate: dateFrom, toDate: dateTo });
      setResult(r);
    } catch (e: any) {
      toast(e.message || 'NAV lekérdezés sikertelen.', 'error');
    } finally { setSyncing(false); }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="NAV bejövő számla szinkronizáció">
      <div className="space-y-4">
        {!result ? (
          <>
            <p style={{ fontSize: '13px', color: 'var(--slate)' }}>
              A NAV Online Számla rendszeréből lekérdezzük a neked címzett számlákat és automatikusan rögzítjük.
            </p>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <label className="form-label">Időszak kezdete</label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              </div>
              <span style={{ marginTop: '20px', color: 'var(--slate)' }}>—</span>
              <div style={{ flex: 1 }}>
                <label className="form-label">Időszak vége</label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
              </div>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--slate)', background: 'var(--mist)', padding: '8px 12px', borderRadius: 'var(--r)' }}>
              Max 35 napos időszak kérdezhető le egyszerre. A duplikátumokat automatikusan kihagyjuk.
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: '10px' }}>✅</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--navy)', marginBottom: '12px' }}>Szinkronizáció befejezve</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
              <div style={{ padding: '10px', background: '#f0fdf4', borderRadius: 'var(--r)', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 800, color: '#16a34a' }}>{result.processed}</div>
                <div style={{ fontSize: '11px', color: '#166534' }}>Új számla</div>
              </div>
              <div style={{ padding: '10px', background: 'var(--mist)', borderRadius: 'var(--r)', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--slate)' }}>{result.skipped}</div>
                <div style={{ fontSize: '11px', color: 'var(--slate)' }}>Kihagyva</div>
              </div>
              <div style={{ padding: '10px', background: result.errors.length > 0 ? '#fef2f2' : 'var(--mist)', borderRadius: 'var(--r)', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 800, color: result.errors.length > 0 ? '#dc2626' : 'var(--slate)' }}>{result.errors.length}</div>
                <div style={{ fontSize: '11px', color: result.errors.length > 0 ? '#991b1b' : 'var(--slate)' }}>Hiba</div>
              </div>
            </div>
            {result.errors.length > 0 && (
              <div style={{ fontSize: '11px', color: '#991b1b', textAlign: 'left', background: '#fef2f2', padding: '8px 12px', borderRadius: 'var(--r)', maxHeight: '100px', overflowY: 'auto' }}>
                {result.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '14px', borderTop: '1px solid var(--line)', marginTop: '14px' }}>
        {!result ? (
          <>
            <Button variant="ghost" onClick={onClose}>Mégse</Button>
            <Button onClick={handleSync} loading={syncing}>Szinkronizáció indítása</Button>
          </>
        ) : (
          <Button onClick={onComplete}>Bezárás</Button>
        )}
      </div>
    </Modal>
  );
}

// ── Manuális rögzítés modal ──

function ManualEntryModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [partner, setPartner] = useState<{ id: string; name: string } | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');
  const [netAmount, setNetAmount] = useState(0);
  const [vatAmount, setVatAmount] = useState(0);
  const [grossAmount, setGrossAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('transfer');

  // Auto bruttó számítás
  useEffect(() => { setGrossAmount(netAmount + vatAmount); }, [netAmount, vatAmount]);

  async function handleSave() {
    if (!partner) { toast('Válassz beszállítót.', 'error'); return; }
    if (!invoiceNumber) { toast('A számla sorszáma kötelező.', 'error'); return; }
    setSaving(true);
    try {
      await post('/incoming-invoices', {
        supplierPartnerId: partner.id,
        supplierInvoiceNumber: invoiceNumber,
        issueDate,
        performanceDate: issueDate,
        dueDate: dueDate || issueDate,
        paymentMethod,
        subtotalNet: netAmount,
        subtotalVat: vatAmount,
        subtotalGross: grossAmount,
        lines: [{ description: 'Bejövő számla tétel', quantity: 1, unitPriceNet: netAmount, vatRate: vatAmount > 0 ? '27' : '0' }],
      });
      toast('Bejövő számla rögzítve.', 'success');
      onCreated();
    } catch (e: any) { toast(e.message || 'Mentés sikertelen.', 'error'); }
    finally { setSaving(false); }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="+ Bejövő számla rögzítése" size="lg">
      <div className="space-y-4" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        <PartnerAutocomplete label="Beszállító *" value={partner} onChange={setPartner} placeholder="Keress beszállítót..." required />
        <Input label="Számla sorszáma *" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="INV-2026-001" />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
          <Input label="Kibocsátás *" type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} />
          <Input label="Esedékesség" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          <div>
            <label className="form-label">Fizetési mód</label>
            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="form-input">
              <option value="transfer">Átutalás</option>
              <option value="cash">Készpénz</option>
              <option value="card">Bankkártya</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px' }}>
          <MoneyInput label="Nettó (Ft) *" value={netAmount} onChange={setNetAmount} />
          <MoneyInput label="ÁFA (Ft)" value={vatAmount} onChange={setVatAmount} />
          <MoneyInput label="Bruttó (Ft)" value={grossAmount} onChange={setGrossAmount} />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '14px', borderTop: '1px solid var(--line)', marginTop: '14px' }}>
        <Button variant="ghost" onClick={onClose}>Mégse</Button>
        <Button onClick={handleSave} loading={saving} disabled={!partner || !invoiceNumber}>Rögzítés</Button>
      </div>
    </Modal>
  );
}
